/**
 * Term Sheets Domain Seed Data
 *
 * Contains all seed data for loan proposals/term sheets:
 * - Term Sheets (loan proposals sent to consumers)
 * - Various loan products (Bridge, Fix & Flip, DSCR, Ground Up, Commercial)
 *
 * Term sheets represent formal loan proposals with rates, terms, and conditions
 * that borrowers can review, accept, or negotiate.
 */

import { seedId } from '../utils';
import { CONSUMER_IDS } from './consumers';
import { BUSINESS_IDS } from './tenants';
import { USER_IDS } from './users';

// =============================================================================
// TERM SHEET IDs
// =============================================================================

export const TERM_SHEET_IDS = {
  // OpsMatrix Capital - VIP Tier (Marcus Thompson, Robert Chen)
  MARCUS_BRIDGE_500K: seedId('termsheet-marcus-bridge', 1),
  MARCUS_PORTFOLIO_2M: seedId('termsheet-marcus-portfolio', 2),
  MARCUS_BRIDGE_650K: seedId('termsheet-marcus-bridge', 3),
  ROBERT_DSCR_1_2M: seedId('termsheet-robert-dscr', 1),
  ROBERT_COMMERCIAL_3M: seedId('termsheet-robert-commercial', 2),
  ROBERT_BRIDGE_800K: seedId('termsheet-robert-bridge', 3),

  // OpsMatrix Capital - Trusted Tier (Alicia, David, Maria)
  ALICIA_DSCR_750K: seedId('termsheet-alicia-dscr', 1),
  ALICIA_FIXFLIP_400K: seedId('termsheet-alicia-fixflip', 2),
  DAVID_BRIDGE_350K: seedId('termsheet-david-bridge', 1),
  DAVID_DSCR_500K: seedId('termsheet-david-dscr', 2),

  // OpsMatrix Capital - Established Tier (Michael, Jessica, Christopher, Amanda)
  MICHAEL_BRIDGE_280K: seedId('termsheet-michael-bridge', 1),
  MICHAEL_REFI_320K: seedId('termsheet-michael-refi', 2),
  JESSICA_FIXFLIP_300K: seedId('termsheet-jessica-fixflip', 1),
  JESSICA_BRIDGE_425K: seedId('termsheet-jessica-bridge', 2),
  AMANDA_BRIDGE_200K: seedId('termsheet-amanda-bridge', 1),
  CHRISTOPHER_FIXFLIP_350K: seedId('termsheet-chris-fixflip', 1),
  CHRISTOPHER_GROUND_UP_1M: seedId('termsheet-chris-groundup', 2),

  // OpsMatrix Capital - Standard Tier (Jennifer, Kevin, Stephanie)
  JENNIFER_FIXFLIP_280K: seedId('termsheet-jennifer-fixflip', 1),
  KEVIN_BRIDGE_250K: seedId('termsheet-kevin-bridge', 1),
  KEVIN_DSCR_320K: seedId('termsheet-kevin-dscr', 2),
  STEPHANIE_BRIDGE_150K: seedId('termsheet-stephanie-bridge', 1),

  // One Sharpe Capital proposals
  MARIA_CONSTRUCTION_1_5M: seedId('termsheet-maria-construction', 1),
  MARIA_DSCR_600K: seedId('termsheet-maria-dscr', 2),
  MARIA_BRIDGE_450K: seedId('termsheet-maria-bridge', 3),

  // Partner-submitted term sheets (channel partners)
  PREMIER_CLIENT_BRIDGE_500K: seedId('termsheet-premier-client', 1),
  AUSTIN_CLIENT_FIXFLIP_380K: seedId('termsheet-austin-client', 1),
  FLORIDA_CLIENT_DSCR_720K: seedId('termsheet-florida-client', 1),
} as const;

export type TermSheetId = (typeof TERM_SHEET_IDS)[keyof typeof TERM_SHEET_IDS];

// =============================================================================
// LOAN PRODUCT TYPES
// =============================================================================

export const LOAN_PRODUCTS = {
  BRIDGE: 'bridge',
  FIX_AND_FLIP: 'fix_and_flip',
  DSCR: 'dscr',
  GROUND_UP: 'ground_up',
  COMMERCIAL: 'commercial',
  CONSTRUCTION: 'construction',
  REFINANCE: 'refinance',
} as const;

export type LoanProduct = (typeof LOAN_PRODUCTS)[keyof typeof LOAN_PRODUCTS];

// =============================================================================
// TERM SHEET STATUSES
// =============================================================================

export const SEED_TERM_SHEET_STATUSES = {
  DRAFT: 'draft',
  SENT: 'sent',
  VIEWED: 'viewed',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
  EXPIRED: 'expired',
  NEGOTIATING: 'negotiating',
  SUPERSEDED: 'superseded',
} as const;

export type SeedTermSheetStatus = (typeof SEED_TERM_SHEET_STATUSES)[keyof typeof SEED_TERM_SHEET_STATUSES];

// =============================================================================
// TERM SHEET SEED DATA
// =============================================================================

export interface TermSheetSeedData {
  id: string;
  businessId: string;
  consumerId: string;
  /** User who created the term sheet */
  createdById: string;
  /** Loan product type */
  loanProduct: LoanProduct;
  /** Current status of the term sheet */
  status: SeedTermSheetStatus;
  /** Property address */
  propertyAddress: string;
  /** Property city */
  propertyCity: string;
  /** Property state (2-letter code) */
  propertyState: string;
  /** Property type */
  propertyType: 'single_family' | 'multi_family' | 'commercial' | 'mixed_use' | 'land';
  /** Loan amount in dollars */
  loanAmount: number;
  /** Interest rate as percentage (e.g., 9.5 = 9.5%) */
  interestRate: number;
  /** Loan term in months */
  termMonths: number;
  /** Origination fee as percentage (e.g., 2.0 = 2 points) */
  originationFee: number;
  /** Loan-to-Value ratio as percentage (e.g., 75 = 75% LTV) */
  ltv: number;
  /** After-Repair Value for fix & flip (optional) */
  arv?: number;
  /** As-Is Value */
  asIsValue?: number;
  /** Rehab/construction budget (optional) */
  rehabBudget?: number;
  /** Monthly payment estimate */
  monthlyPayment?: number;
  /** Prepayment penalty terms (optional) */
  prepaymentPenalty?: string;
  /** Extension options (optional) */
  extensionOptions?: string;
  /** Special terms or notes */
  specialTerms?: string;
  /** Days ago the term sheet was created */
  daysAgo: number;
  /** Days ago the term sheet expires (from creation) */
  expiresInDays: number;
}

export const TERM_SHEETS: Record<keyof typeof TERM_SHEET_IDS, TermSheetSeedData> = {
  // ==========================================================================
  // VIP TIER - Marcus Thompson (12 completed deals)
  // ==========================================================================
  MARCUS_BRIDGE_500K: {
    id: TERM_SHEET_IDS.MARCUS_BRIDGE_500K,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.MARCUS_THOMPSON,
    createdById: USER_IDS.RACHEL_MARTINEZ,
    loanProduct: LOAN_PRODUCTS.BRIDGE,
    status: SEED_TERM_SHEET_STATUSES.ACCEPTED,
    propertyAddress: '4521 Live Oak Boulevard',
    propertyCity: 'Austin',
    propertyState: 'TX',
    propertyType: 'single_family',
    loanAmount: 500000,
    interestRate: 8.75,
    termMonths: 12,
    originationFee: 1.5,
    ltv: 70,
    asIsValue: 715000,
    monthlyPayment: 3646,
    prepaymentPenalty: 'None after 3 months',
    extensionOptions: '2x 3-month extensions at 0.5% fee each',
    specialTerms: 'VIP rate - reduced from standard 9.25%. Fast-track underwriting.',
    daysAgo: 45,
    expiresInDays: 30,
  },
  MARCUS_PORTFOLIO_2M: {
    id: TERM_SHEET_IDS.MARCUS_PORTFOLIO_2M,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.MARCUS_THOMPSON,
    createdById: USER_IDS.SARAH_CHEN,
    loanProduct: LOAN_PRODUCTS.BRIDGE,
    status: SEED_TERM_SHEET_STATUSES.SENT,
    propertyAddress: '5-Property Portfolio - Various',
    propertyCity: 'Austin',
    propertyState: 'TX',
    propertyType: 'multi_family',
    loanAmount: 2500000,
    interestRate: 8.5,
    termMonths: 18,
    originationFee: 1.25,
    ltv: 65,
    asIsValue: 3850000,
    monthlyPayment: 17708,
    prepaymentPenalty: 'None',
    extensionOptions: '1x 6-month extension at 0.75% fee',
    specialTerms: 'Portfolio rate discount. Cross-collateralized. VIP client - priority processing.',
    daysAgo: 3,
    expiresInDays: 14,
  },
  MARCUS_BRIDGE_650K: {
    id: TERM_SHEET_IDS.MARCUS_BRIDGE_650K,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.MARCUS_THOMPSON,
    createdById: USER_IDS.RACHEL_MARTINEZ,
    loanProduct: LOAN_PRODUCTS.BRIDGE,
    status: SEED_TERM_SHEET_STATUSES.SUPERSEDED,
    propertyAddress: '8912 Westlake Hills Drive',
    propertyCity: 'Austin',
    propertyState: 'TX',
    propertyType: 'single_family',
    loanAmount: 650000,
    interestRate: 9.0,
    termMonths: 12,
    originationFee: 1.75,
    ltv: 72,
    asIsValue: 903000,
    monthlyPayment: 4875,
    prepaymentPenalty: 'None after 6 months',
    specialTerms: 'Superseded by revised terms with lower rate',
    daysAgo: 60,
    expiresInDays: 30,
  },

  // ==========================================================================
  // VIP TIER - Robert Chen (15 completed deals)
  // ==========================================================================
  ROBERT_DSCR_1_2M: {
    id: TERM_SHEET_IDS.ROBERT_DSCR_1_2M,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.ROBERT_CHEN,
    createdById: USER_IDS.JAMES_WILSON,
    loanProduct: LOAN_PRODUCTS.DSCR,
    status: SEED_TERM_SHEET_STATUSES.ACCEPTED,
    propertyAddress: '2345 Memorial Park Plaza',
    propertyCity: 'Houston',
    propertyState: 'TX',
    propertyType: 'multi_family',
    loanAmount: 1200000,
    interestRate: 7.25,
    termMonths: 360,
    originationFee: 1.0,
    ltv: 75,
    asIsValue: 1600000,
    monthlyPayment: 8186,
    prepaymentPenalty: '5-4-3-2-1% declining over 5 years',
    specialTerms: 'DSCR 1.35. 30-year fixed. Interest-only option available first 2 years.',
    daysAgo: 30,
    expiresInDays: 21,
  },
  ROBERT_COMMERCIAL_3M: {
    id: TERM_SHEET_IDS.ROBERT_COMMERCIAL_3M,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.ROBERT_CHEN,
    createdById: USER_IDS.SARAH_CHEN,
    loanProduct: LOAN_PRODUCTS.COMMERCIAL,
    status: SEED_TERM_SHEET_STATUSES.NEGOTIATING,
    propertyAddress: '500 Commerce Street',
    propertyCity: 'Houston',
    propertyState: 'TX',
    propertyType: 'commercial',
    loanAmount: 3000000,
    interestRate: 7.75,
    termMonths: 120,
    originationFee: 1.5,
    ltv: 65,
    asIsValue: 4615000,
    monthlyPayment: 35850,
    prepaymentPenalty: 'Yield maintenance for 5 years',
    specialTerms: 'NNN retail strip. 92% occupied. Negotiating rate reduction based on tenant quality.',
    daysAgo: 7,
    expiresInDays: 21,
  },
  ROBERT_BRIDGE_800K: {
    id: TERM_SHEET_IDS.ROBERT_BRIDGE_800K,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.ROBERT_CHEN,
    createdById: USER_IDS.RACHEL_MARTINEZ,
    loanProduct: LOAN_PRODUCTS.BRIDGE,
    status: SEED_TERM_SHEET_STATUSES.EXPIRED,
    propertyAddress: '1100 Galleria Tower',
    propertyCity: 'Houston',
    propertyState: 'TX',
    propertyType: 'commercial',
    loanAmount: 800000,
    interestRate: 9.25,
    termMonths: 12,
    originationFee: 2.0,
    ltv: 70,
    asIsValue: 1143000,
    monthlyPayment: 6167,
    prepaymentPenalty: 'None',
    specialTerms: 'Expired - borrower found alternative financing',
    daysAgo: 90,
    expiresInDays: 30,
  },

  // ==========================================================================
  // TRUSTED TIER - Alicia Fernandez (7 completed deals)
  // ==========================================================================
  ALICIA_DSCR_750K: {
    id: TERM_SHEET_IDS.ALICIA_DSCR_750K,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.ALICIA_FERNANDEZ,
    createdById: USER_IDS.JAMES_WILSON,
    loanProduct: LOAN_PRODUCTS.DSCR,
    status: SEED_TERM_SHEET_STATUSES.VIEWED,
    propertyAddress: '7890 Bay Harbor Drive',
    propertyCity: 'Tampa',
    propertyState: 'FL',
    propertyType: 'multi_family',
    loanAmount: 750000,
    interestRate: 7.5,
    termMonths: 360,
    originationFee: 1.25,
    ltv: 75,
    asIsValue: 1000000,
    monthlyPayment: 5244,
    prepaymentPenalty: '3-2-1% declining over 3 years',
    specialTerms: '4-plex in Tampa. DSCR 1.28. Gross rents $8,200/month.',
    daysAgo: 5,
    expiresInDays: 14,
  },
  ALICIA_FIXFLIP_400K: {
    id: TERM_SHEET_IDS.ALICIA_FIXFLIP_400K,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.ALICIA_FERNANDEZ,
    createdById: USER_IDS.MIKE_JOHNSON,
    loanProduct: LOAN_PRODUCTS.FIX_AND_FLIP,
    status: SEED_TERM_SHEET_STATUSES.ACCEPTED,
    propertyAddress: '3456 Palm Beach Road',
    propertyCity: 'Miami',
    propertyState: 'FL',
    propertyType: 'single_family',
    loanAmount: 400000,
    interestRate: 10.5,
    termMonths: 9,
    originationFee: 2.5,
    ltv: 90,
    arv: 650000,
    asIsValue: 320000,
    rehabBudget: 125000,
    monthlyPayment: 3500,
    prepaymentPenalty: 'None',
    extensionOptions: '2x 2-month extensions at $2,500 each',
    specialTerms: '90% of purchase + 100% of rehab. Fast close 7 days.',
    daysAgo: 20,
    expiresInDays: 10,
  },

  // ==========================================================================
  // TRUSTED TIER - David Williams (5 completed deals)
  // ==========================================================================
  DAVID_BRIDGE_350K: {
    id: TERM_SHEET_IDS.DAVID_BRIDGE_350K,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.DAVID_WILLIAMS,
    createdById: USER_IDS.RACHEL_MARTINEZ,
    loanProduct: LOAN_PRODUCTS.BRIDGE,
    status: SEED_TERM_SHEET_STATUSES.ACCEPTED,
    propertyAddress: '5678 Preston Road',
    propertyCity: 'Dallas',
    propertyState: 'TX',
    propertyType: 'single_family',
    loanAmount: 350000,
    interestRate: 9.0,
    termMonths: 12,
    originationFee: 2.0,
    ltv: 70,
    asIsValue: 500000,
    monthlyPayment: 2625,
    prepaymentPenalty: 'None',
    extensionOptions: '1x 3-month extension at $1,750',
    specialTerms: 'Rental property acquisition. Refinance to DSCR within 6 months.',
    daysAgo: 35,
    expiresInDays: 14,
  },
  DAVID_DSCR_500K: {
    id: TERM_SHEET_IDS.DAVID_DSCR_500K,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.DAVID_WILLIAMS,
    createdById: USER_IDS.JAMES_WILSON,
    loanProduct: LOAN_PRODUCTS.DSCR,
    status: SEED_TERM_SHEET_STATUSES.DRAFT,
    propertyAddress: '1234 Mockingbird Lane',
    propertyCity: 'Dallas',
    propertyState: 'TX',
    propertyType: 'single_family',
    loanAmount: 500000,
    interestRate: 7.75,
    termMonths: 360,
    originationFee: 1.0,
    ltv: 75,
    asIsValue: 667000,
    monthlyPayment: 3578,
    prepaymentPenalty: '3% first year, 2% second, 1% third',
    specialTerms: 'Draft pending appraisal. DSCR estimated at 1.22.',
    daysAgo: 2,
    expiresInDays: 21,
  },

  // ==========================================================================
  // ESTABLISHED TIER - Michael Brown (3 completed deals)
  // ==========================================================================
  MICHAEL_BRIDGE_280K: {
    id: TERM_SHEET_IDS.MICHAEL_BRIDGE_280K,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.MICHAEL_BROWN,
    createdById: USER_IDS.MIKE_JOHNSON,
    loanProduct: LOAN_PRODUCTS.BRIDGE,
    status: SEED_TERM_SHEET_STATUSES.ACCEPTED,
    propertyAddress: '8901 Westheimer Road',
    propertyCity: 'Houston',
    propertyState: 'TX',
    propertyType: 'single_family',
    loanAmount: 280000,
    interestRate: 9.5,
    termMonths: 12,
    originationFee: 2.0,
    ltv: 70,
    asIsValue: 400000,
    monthlyPayment: 2217,
    prepaymentPenalty: 'None',
    specialTerms: 'Standard terms. First-time bridge loan with us.',
    daysAgo: 60,
    expiresInDays: 14,
  },
  MICHAEL_REFI_320K: {
    id: TERM_SHEET_IDS.MICHAEL_REFI_320K,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.MICHAEL_BROWN,
    createdById: USER_IDS.JAMES_WILSON,
    loanProduct: LOAN_PRODUCTS.REFINANCE,
    status: SEED_TERM_SHEET_STATUSES.SENT,
    propertyAddress: '4521 Memorial Heights',
    propertyCity: 'Houston',
    propertyState: 'TX',
    propertyType: 'single_family',
    loanAmount: 320000,
    interestRate: 7.25,
    termMonths: 360,
    originationFee: 1.0,
    ltv: 70,
    asIsValue: 457000,
    monthlyPayment: 2183,
    prepaymentPenalty: '3-2-1% declining',
    specialTerms: 'Rate-and-term refinance from 8.5% bridge. Cash-out available up to 75% LTV.',
    daysAgo: 8,
    expiresInDays: 21,
  },

  // ==========================================================================
  // ESTABLISHED TIER - Jessica Lee (2 completed deals)
  // ==========================================================================
  JESSICA_FIXFLIP_300K: {
    id: TERM_SHEET_IDS.JESSICA_FIXFLIP_300K,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.JESSICA_LEE,
    createdById: USER_IDS.MIKE_JOHNSON,
    loanProduct: LOAN_PRODUCTS.FIX_AND_FLIP,
    status: SEED_TERM_SHEET_STATUSES.ACCEPTED,
    propertyAddress: '2847 Maple Street',
    propertyCity: 'Dallas',
    propertyState: 'TX',
    propertyType: 'single_family',
    loanAmount: 300000,
    interestRate: 11.0,
    termMonths: 9,
    originationFee: 2.5,
    ltv: 85,
    arv: 450000,
    asIsValue: 220000,
    rehabBudget: 95000,
    monthlyPayment: 2750,
    prepaymentPenalty: 'None',
    extensionOptions: '1x 3-month extension at $1,500',
    specialTerms: 'Draw schedule: 4 draws. First draw at closing for demo/permits.',
    daysAgo: 25,
    expiresInDays: 10,
  },
  JESSICA_BRIDGE_425K: {
    id: TERM_SHEET_IDS.JESSICA_BRIDGE_425K,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.JESSICA_LEE,
    createdById: USER_IDS.RACHEL_MARTINEZ,
    loanProduct: LOAN_PRODUCTS.BRIDGE,
    status: SEED_TERM_SHEET_STATUSES.REJECTED,
    propertyAddress: '7654 Park Lane',
    propertyCity: 'Fort Worth',
    propertyState: 'TX',
    propertyType: 'single_family',
    loanAmount: 425000,
    interestRate: 9.75,
    termMonths: 12,
    originationFee: 2.5,
    ltv: 75,
    asIsValue: 567000,
    monthlyPayment: 3453,
    prepaymentPenalty: 'None',
    specialTerms: 'Rejected - borrower chose competitor with lower rate.',
    daysAgo: 40,
    expiresInDays: 14,
  },

  // ==========================================================================
  // ESTABLISHED TIER - Amanda Wilson (2 completed deals)
  // ==========================================================================
  AMANDA_BRIDGE_200K: {
    id: TERM_SHEET_IDS.AMANDA_BRIDGE_200K,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.AMANDA_WILSON,
    createdById: USER_IDS.MIKE_JOHNSON,
    loanProduct: LOAN_PRODUCTS.BRIDGE,
    status: SEED_TERM_SHEET_STATUSES.SENT,
    propertyAddress: '3456 Oak Hill Drive',
    propertyCity: 'Dallas',
    propertyState: 'TX',
    propertyType: 'single_family',
    loanAmount: 200000,
    interestRate: 9.5,
    termMonths: 12,
    originationFee: 2.0,
    ltv: 65,
    asIsValue: 308000,
    monthlyPayment: 1583,
    prepaymentPenalty: 'None',
    specialTerms: 'Conservative LTV due to market conditions. Payment extension request pending.',
    daysAgo: 12,
    expiresInDays: 14,
  },

  // ==========================================================================
  // ESTABLISHED TIER - Christopher Davis (4 completed deals)
  // ==========================================================================
  CHRISTOPHER_FIXFLIP_350K: {
    id: TERM_SHEET_IDS.CHRISTOPHER_FIXFLIP_350K,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    consumerId: CONSUMER_IDS.CHRISTOPHER_DAVIS,
    createdById: USER_IDS.SHARPE_ADMIN,
    loanProduct: LOAN_PRODUCTS.FIX_AND_FLIP,
    status: SEED_TERM_SHEET_STATUSES.ACCEPTED,
    propertyAddress: '1892 Cedar Lane',
    propertyCity: 'Fort Worth',
    propertyState: 'TX',
    propertyType: 'single_family',
    loanAmount: 350000,
    interestRate: 10.75,
    termMonths: 9,
    originationFee: 2.5,
    ltv: 88,
    arv: 520000,
    asIsValue: 250000,
    rehabBudget: 110000,
    monthlyPayment: 3135,
    prepaymentPenalty: 'None',
    extensionOptions: '2x 2-month extensions at $1,750 each',
    specialTerms: 'Final draw pending. Renovation 95% complete. Photos submitted.',
    daysAgo: 75,
    expiresInDays: 10,
  },
  CHRISTOPHER_GROUND_UP_1M: {
    id: TERM_SHEET_IDS.CHRISTOPHER_GROUND_UP_1M,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    consumerId: CONSUMER_IDS.CHRISTOPHER_DAVIS,
    createdById: USER_IDS.SHARPE_ADMIN,
    loanProduct: LOAN_PRODUCTS.GROUND_UP,
    status: SEED_TERM_SHEET_STATUSES.DRAFT,
    propertyAddress: '5500 Vacant Land - Riverside',
    propertyCity: 'Arlington',
    propertyState: 'TX',
    propertyType: 'land',
    loanAmount: 1000000,
    interestRate: 11.5,
    termMonths: 18,
    originationFee: 3.0,
    ltv: 80,
    arv: 1500000,
    asIsValue: 250000,
    rehabBudget: 1000000,
    monthlyPayment: 9583,
    prepaymentPenalty: 'None',
    extensionOptions: '2x 3-month extensions at $5,000 each',
    specialTerms: 'Ground-up construction. Spec home. 65% LTC. Draw schedule: 8 draws.',
    daysAgo: 1,
    expiresInDays: 21,
  },

  // ==========================================================================
  // STANDARD TIER - Jennifer Martinez (0 completed deals)
  // ==========================================================================
  JENNIFER_FIXFLIP_280K: {
    id: TERM_SHEET_IDS.JENNIFER_FIXFLIP_280K,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.JENNIFER_MARTINEZ,
    createdById: USER_IDS.MIKE_JOHNSON,
    loanProduct: LOAN_PRODUCTS.FIX_AND_FLIP,
    status: SEED_TERM_SHEET_STATUSES.SENT,
    propertyAddress: '4567 Congress Avenue',
    propertyCity: 'Austin',
    propertyState: 'TX',
    propertyType: 'single_family',
    loanAmount: 280000,
    interestRate: 11.5,
    termMonths: 9,
    originationFee: 3.0,
    ltv: 85,
    arv: 410000,
    asIsValue: 230000,
    rehabBudget: 50000,
    monthlyPayment: 2683,
    prepaymentPenalty: 'None',
    extensionOptions: '1x 2-month extension at $1,400',
    specialTerms: 'First-time borrower. Reduced rehab holdback (75%). Mentor required on job site.',
    daysAgo: 6,
    expiresInDays: 10,
  },

  // ==========================================================================
  // STANDARD TIER - Kevin Johnson (0 completed deals)
  // ==========================================================================
  KEVIN_BRIDGE_250K: {
    id: TERM_SHEET_IDS.KEVIN_BRIDGE_250K,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.KEVIN_JOHNSON,
    createdById: USER_IDS.RACHEL_MARTINEZ,
    loanProduct: LOAN_PRODUCTS.BRIDGE,
    status: SEED_TERM_SHEET_STATUSES.VIEWED,
    propertyAddress: '8900 Heights Boulevard',
    propertyCity: 'Houston',
    propertyState: 'TX',
    propertyType: 'single_family',
    loanAmount: 250000,
    interestRate: 9.75,
    termMonths: 12,
    originationFee: 2.5,
    ltv: 70,
    asIsValue: 357000,
    monthlyPayment: 2031,
    prepaymentPenalty: 'None',
    specialTerms: 'Pre-qualification in progress. Pending credit score verification.',
    daysAgo: 4,
    expiresInDays: 14,
  },
  KEVIN_DSCR_320K: {
    id: TERM_SHEET_IDS.KEVIN_DSCR_320K,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.KEVIN_JOHNSON,
    createdById: USER_IDS.JAMES_WILSON,
    loanProduct: LOAN_PRODUCTS.DSCR,
    status: SEED_TERM_SHEET_STATUSES.DRAFT,
    propertyAddress: '2100 Montrose Boulevard',
    propertyCity: 'Houston',
    propertyState: 'TX',
    propertyType: 'single_family',
    loanAmount: 320000,
    interestRate: 8.0,
    termMonths: 360,
    originationFee: 1.5,
    ltv: 75,
    asIsValue: 427000,
    monthlyPayment: 2348,
    prepaymentPenalty: '3-2-1% declining',
    specialTerms: 'Draft pending income documentation. DSCR estimated 1.18.',
    daysAgo: 1,
    expiresInDays: 21,
  },

  // ==========================================================================
  // STANDARD TIER - Stephanie Garcia (0 completed deals)
  // ==========================================================================
  STEPHANIE_BRIDGE_150K: {
    id: TERM_SHEET_IDS.STEPHANIE_BRIDGE_150K,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    consumerId: CONSUMER_IDS.STEPHANIE_GARCIA,
    createdById: USER_IDS.SHARPE_ADMIN,
    loanProduct: LOAN_PRODUCTS.BRIDGE,
    status: SEED_TERM_SHEET_STATUSES.SENT,
    propertyAddress: '3200 San Pedro Avenue',
    propertyCity: 'San Antonio',
    propertyState: 'TX',
    propertyType: 'single_family',
    loanAmount: 150000,
    interestRate: 10.0,
    termMonths: 12,
    originationFee: 2.5,
    ltv: 65,
    asIsValue: 231000,
    monthlyPayment: 1250,
    prepaymentPenalty: 'None',
    specialTerms: 'First-time investor. BRRRR strategy. Conservative LTV. Liquidity reserves verified.',
    daysAgo: 3,
    expiresInDays: 14,
  },

  // ==========================================================================
  // ONE SHARPE CAPITAL - Maria Santos (6 completed deals)
  // ==========================================================================
  MARIA_CONSTRUCTION_1_5M: {
    id: TERM_SHEET_IDS.MARIA_CONSTRUCTION_1_5M,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    consumerId: CONSUMER_IDS.MARIA_SANTOS,
    createdById: USER_IDS.SHARPE_ADMIN,
    loanProduct: LOAN_PRODUCTS.CONSTRUCTION,
    status: SEED_TERM_SHEET_STATUSES.NEGOTIATING,
    propertyAddress: '2800 Alamo Heights - Vacant Lot',
    propertyCity: 'San Antonio',
    propertyState: 'TX',
    propertyType: 'land',
    loanAmount: 1500000,
    interestRate: 11.0,
    termMonths: 24,
    originationFee: 2.5,
    ltv: 75,
    arv: 2400000,
    asIsValue: 400000,
    rehabBudget: 1400000,
    monthlyPayment: 13750,
    prepaymentPenalty: 'None',
    extensionOptions: '2x 6-month extensions at $7,500 each',
    specialTerms: '8-unit multifamily construction. Negotiating interest reserve structure.',
    daysAgo: 10,
    expiresInDays: 21,
  },
  MARIA_DSCR_600K: {
    id: TERM_SHEET_IDS.MARIA_DSCR_600K,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    consumerId: CONSUMER_IDS.MARIA_SANTOS,
    createdById: USER_IDS.SHARPE_ADMIN,
    loanProduct: LOAN_PRODUCTS.DSCR,
    status: SEED_TERM_SHEET_STATUSES.ACCEPTED,
    propertyAddress: '1500 River Walk Plaza',
    propertyCity: 'San Antonio',
    propertyState: 'TX',
    propertyType: 'multi_family',
    loanAmount: 600000,
    interestRate: 7.5,
    termMonths: 360,
    originationFee: 1.0,
    ltv: 75,
    asIsValue: 800000,
    monthlyPayment: 4195,
    prepaymentPenalty: '3-2-1% declining',
    specialTerms: 'DSCR 1.32. 4-unit property. All units leased. Trusted client rate.',
    daysAgo: 45,
    expiresInDays: 21,
  },
  MARIA_BRIDGE_450K: {
    id: TERM_SHEET_IDS.MARIA_BRIDGE_450K,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    consumerId: CONSUMER_IDS.MARIA_SANTOS,
    createdById: USER_IDS.SHARPE_ADMIN,
    loanProduct: LOAN_PRODUCTS.BRIDGE,
    status: SEED_TERM_SHEET_STATUSES.EXPIRED,
    propertyAddress: '4100 Fredericksburg Road',
    propertyCity: 'San Antonio',
    propertyState: 'TX',
    propertyType: 'single_family',
    loanAmount: 450000,
    interestRate: 9.0,
    termMonths: 12,
    originationFee: 1.75,
    ltv: 72,
    asIsValue: 625000,
    monthlyPayment: 3375,
    prepaymentPenalty: 'None',
    specialTerms: 'Expired - property sold to another buyer.',
    daysAgo: 75,
    expiresInDays: 30,
  },

  // ==========================================================================
  // PARTNER-SUBMITTED TERM SHEETS
  // ==========================================================================
  PREMIER_CLIENT_BRIDGE_500K: {
    id: TERM_SHEET_IDS.PREMIER_CLIENT_BRIDGE_500K,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.MARCUS_THOMPSON, // Partner's client
    createdById: USER_IDS.RACHEL_MARTINEZ,
    loanProduct: LOAN_PRODUCTS.BRIDGE,
    status: SEED_TERM_SHEET_STATUSES.SENT,
    propertyAddress: '6789 Brickell Avenue',
    propertyCity: 'Miami',
    propertyState: 'FL',
    propertyType: 'single_family',
    loanAmount: 500000,
    interestRate: 9.25,
    termMonths: 12,
    originationFee: 2.0,
    ltv: 70,
    asIsValue: 715000,
    monthlyPayment: 3854,
    prepaymentPenalty: 'None',
    specialTerms: 'Submitted via Premier Mortgage Solutions (channel partner). 1.5% partner commission.',
    daysAgo: 5,
    expiresInDays: 14,
  },
  AUSTIN_CLIENT_FIXFLIP_380K: {
    id: TERM_SHEET_IDS.AUSTIN_CLIENT_FIXFLIP_380K,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.MICHAEL_BROWN, // Partner's client
    createdById: USER_IDS.MIKE_JOHNSON,
    loanProduct: LOAN_PRODUCTS.FIX_AND_FLIP,
    status: SEED_TERM_SHEET_STATUSES.VIEWED,
    propertyAddress: '2345 South Lamar Boulevard',
    propertyCity: 'Austin',
    propertyState: 'TX',
    propertyType: 'single_family',
    loanAmount: 380000,
    interestRate: 10.75,
    termMonths: 9,
    originationFee: 2.5,
    ltv: 87,
    arv: 550000,
    asIsValue: 290000,
    rehabBudget: 95000,
    monthlyPayment: 3404,
    prepaymentPenalty: 'None',
    specialTerms: 'Submitted via Austin Mortgage Group (channel partner). 1.25% partner commission.',
    daysAgo: 3,
    expiresInDays: 10,
  },
  FLORIDA_CLIENT_DSCR_720K: {
    id: TERM_SHEET_IDS.FLORIDA_CLIENT_DSCR_720K,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.ALICIA_FERNANDEZ, // Partner's client
    createdById: USER_IDS.JAMES_WILSON,
    loanProduct: LOAN_PRODUCTS.DSCR,
    status: SEED_TERM_SHEET_STATUSES.ACCEPTED,
    propertyAddress: '8800 Clearwater Beach Drive',
    propertyCity: 'Clearwater',
    propertyState: 'FL',
    propertyType: 'multi_family',
    loanAmount: 720000,
    interestRate: 7.25,
    termMonths: 360,
    originationFee: 1.0,
    ltv: 75,
    asIsValue: 960000,
    monthlyPayment: 4912,
    prepaymentPenalty: '3-2-1% declining',
    specialTerms: 'Submitted via Florida First Lending (channel partner). 1.35% partner commission. DSCR 1.31.',
    daysAgo: 20,
    expiresInDays: 21,
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get all term sheets as an array for seeding
 */
export function getAllTermSheets(): TermSheetSeedData[] {
  return Object.values(TERM_SHEETS);
}

/**
 * Get term sheets by business
 */
export function getTermSheetsByBusiness(businessId: string): TermSheetSeedData[] {
  return Object.values(TERM_SHEETS).filter((t) => t.businessId === businessId);
}

/**
 * Get term sheets by consumer
 */
export function getTermSheetsByConsumer(consumerId: string): TermSheetSeedData[] {
  return Object.values(TERM_SHEETS).filter((t) => t.consumerId === consumerId);
}

/**
 * Get term sheets by status
 */
export function getTermSheetsByStatus(status: SeedTermSheetStatus): TermSheetSeedData[] {
  return Object.values(TERM_SHEETS).filter((t) => t.status === status);
}

/**
 * Get term sheets by loan product
 */
export function getTermSheetsByProduct(product: LoanProduct): TermSheetSeedData[] {
  return Object.values(TERM_SHEETS).filter((t) => t.loanProduct === product);
}

/**
 * Get active term sheets (sent, viewed, negotiating)
 */
export function getActiveTermSheets(): TermSheetSeedData[] {
  const activeStatuses: SeedTermSheetStatus[] = [
    SEED_TERM_SHEET_STATUSES.SENT,
    SEED_TERM_SHEET_STATUSES.VIEWED,
    SEED_TERM_SHEET_STATUSES.NEGOTIATING,
  ];
  return Object.values(TERM_SHEETS).filter((t) => activeStatuses.includes(t.status));
}

/**
 * Get term sheets by state
 */
export function getTermSheetsByState(state: string): TermSheetSeedData[] {
  return Object.values(TERM_SHEETS).filter((t) => t.propertyState === state);
}

/**
 * Calculate total pipeline value (all non-expired, non-rejected term sheets)
 */
export function calculatePipelineValue(): number {
  const excludedStatuses: SeedTermSheetStatus[] = [SEED_TERM_SHEET_STATUSES.EXPIRED, SEED_TERM_SHEET_STATUSES.REJECTED];
  return Object.values(TERM_SHEETS)
    .filter((t) => !excludedStatuses.includes(t.status))
    .reduce((sum, t) => sum + t.loanAmount, 0);
}

/**
 * Get term sheet statistics by status
 */
export function getTermSheetStats(): Record<SeedTermSheetStatus, { count: number; totalAmount: number }> {
  const stats: Record<SeedTermSheetStatus, { count: number; totalAmount: number }> = {
    draft: { count: 0, totalAmount: 0 },
    sent: { count: 0, totalAmount: 0 },
    viewed: { count: 0, totalAmount: 0 },
    accepted: { count: 0, totalAmount: 0 },
    rejected: { count: 0, totalAmount: 0 },
    expired: { count: 0, totalAmount: 0 },
    negotiating: { count: 0, totalAmount: 0 },
    superseded: { count: 0, totalAmount: 0 },
  };

  for (const termSheet of Object.values(TERM_SHEETS)) {
    stats[termSheet.status].count++;
    stats[termSheet.status].totalAmount += termSheet.loanAmount;
  }

  return stats;
}
